import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Diagnostics, GasPrices } from '@darkforest_eth/types';
import { Wrapper } from '../../Backend/Utils/Wrapper';
import { EmSpacer, PaddedRecommendedModalWidth, Separator } from '../Components/CoreUI';
import { DisplayGasPrices } from '../Components/DisplayGasPrices';
import { TextPreview } from '../Components/TextPreview';
import { useUIManager } from '../Utils/AppHooks';
import { BooleanSetting, Setting } from '../Utils/SettingsHooks';
import { ModalHook, ModalPane } from '../Views/ModalPane';
import { SpreadApart } from '../Views/PlanetCardComponents';
import { TabbedView } from '../Views/TabbedView';
import { getLocalGasPrices } from '../../Backend/Network/LocalGasPriceAPI';

export function DiagnosticsPane({ hook }: { hook: ModalHook }) {
  const uiManager = useUIManager();
  const [gasPrices, setGasPrices] = useState<GasPrices | null>(null);

  useEffect(() => {
    const fetchGasPrices = async () => {
      const prices = await getLocalGasPrices();
      setGasPrices(prices);
    };

    fetchGasPrices();
    const interval = setInterval(fetchGasPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const diagnostics = uiManager.getDiagnostics();

  return (
    <ModalPane hook={hook} title={'Diagnostics'}>
      <PaddedRecommendedModalWidth>
        <DiagnosticsTabs diagnostics={diagnostics} gasPrices={gasPrices} />
      </PaddedRecommendedModalWidth>
    </ModalPane>
  );
}

function DiagnosticsTabs({
  diagnostics,
  gasPrices
}: {
  diagnostics: Diagnostics;
  gasPrices: GasPrices | null;
}) {
  return (
    <>
      <TabbedView
        tabTitles={['Rendering', 'Networking']}
        tabContents={(tabIndex) => {
          switch (tabIndex) {
            case 0:
              return <RenderingTab diagnostics={diagnostics} />;
            case 1:
              return <NetworkingTab diagnostics={diagnostics} gasPrices={gasPrices} />;
            default:
              return <RenderingTab diagnostics={diagnostics} />;
          }
        }}
      />
    </>
  );
}

function RenderingTab({ diagnostics }: { diagnostics: Diagnostics }) {
  const uiManager = useUIManager();

  return (
    <>
      <Separator />

      <SpreadApart>
        <span>fps</span>
        <span>{Math.floor(diagnostics.fps)}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>visible chunks</span>
        <span>{diagnostics.visibleChunks.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>total chunks</span>
        <span>{diagnostics.totalChunks.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>visible planets</span>
        <span>{diagnostics.visiblePlanets.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>total planets</span>
        <span>{diagnostics.totalPlanets.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>queued chunk writes</span>
        <span>{diagnostics.chunkUpdates.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>viewport</span>
        <span>
          <span>{diagnostics.width.toLocaleString()}</span>
          <span> x </span>
          <span>{diagnostics.height.toLocaleString()}</span>
        </span>
      </SpreadApart>
      <Separator />
      <EmSpacer height={0.5} />
      <BooleanSetting
        uiManager={uiManager}
        setting={Setting.DrawChunkBorders}
        settingDescription='draw chunk borders'
      />
    </>
  );
}

function NetworkingTab({ diagnostics, gasPrices }: { diagnostics: Diagnostics; gasPrices: GasPrices | null }) {
  return (
    <>
      <Separator />

      <SpreadApart>
        <span>rpc url</span>
        <span>
          <TextPreview
            text={diagnostics.rpcUrl}
            unFocusedWidth={'100px'}
            focusedWidth={'100px'}
          />
        </span>
      </SpreadApart>
      <Separator />
      <SpreadApart>
        <span>completed calls</span>
        <span>{diagnostics.totalCalls.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>queued calls</span>
        <span>{diagnostics.callsInQueue.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>completed transactions</span>
        <span>{diagnostics.totalTransactions.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>queued transactions</span>
        <span>{diagnostics.transactionsInQueue.toLocaleString()}</span>
      </SpreadApart>

      <Separator />

      <SpreadApart>
        <span>oracle gas prices</span>
        <span>
          <DisplayGasPrices gasPrices={gasPrices} />
        </span>
      </SpreadApart>
    </>
  );
}
